import React from 'react';
import toast from 'react-hot-toast';
import { cn } from '../../lib/utils';

const formatType = (type) => {
  const labels = {
    'mechanic': 'Mechanic',
    'ancestry': 'Ancestry',
    'community': 'Community',
    'domain-card': 'Domain Card',
    'campaign-frame': 'Campaign Frame',
  };
  return labels[type] || type;
};

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'recently';
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

function HomebrewContentCard({ content, onDelete, onDuplicate, onExport, onEdit }) {
  const handleDelete = () => {
    toast((t) => (
      <div>
        <p className="font-medium">Delete "{content.meta?.name}"?</p>
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => { onDelete(content.id); toast.dismiss(t.id); }}
            className="bg-red-600 text-white px-3 py-1 rounded text-sm"
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="bg-gray-600 text-white px-3 py-1 rounded text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  return (
    <div
      onClick={() => onEdit && onEdit(content)}
      className={cn(
        "p-6 rounded-lg border shadow-2xl transition-all",
        "bg-gradient-to-br from-gray-900 to-black",
        "border-red-700 hover:border-red-600",
        onEdit && "cursor-pointer hover:bg-gray-900/80",
      )}
    >
      {/* Header: name */}
      <div className="mb-2 flex items-center gap-1">
        <h3 className="font-bold text-heading-sm text-white truncate">{content.meta?.name || 'Untitled'}</h3>
        {content.reviewStatus === 'needsReview' && (
          <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="Needs review" />
        )}
        {content.reviewStatus === 'auto' && (
          <span className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" title="Auto-generated" />
        )}
      </div>

      {/* Type + status badges */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
          {formatType(content.type)}
        </span>
        <span className={cn(
          "text-xs px-2 py-0.5 rounded-full",
          content.status === 'ready'
            ? 'bg-green-500/20 text-green-400'
            : 'bg-amber-500/20 text-amber-400'
        )}>
          {content.status === 'ready' ? 'Ready' : 'Draft'}
        </span>
      </div>

      {/* Description */}
      <p className="text-small text-gray-400 line-clamp-2 mb-3">
        {content.meta?.description || 'No description'}
      </p>

      {/* Updated time */}
      <p className="text-xs text-gray-500 mb-3">
        Updated {formatRelativeTime(content.meta?.updatedAt)}
      </p>

      {/* Action buttons */}
      <div className="flex gap-2" onClick={e => e.stopPropagation()}>
        {onEdit && (
          <button onClick={() => onEdit(content)} className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors">Edit</button>
        )}
        <button onClick={() => onExport(content)} className="text-xs text-gray-400 hover:text-white transition-colors">Export</button>
        <button onClick={() => onDuplicate(content.id)} className="text-xs text-gray-400 hover:text-white transition-colors">Duplicate</button>
        <button onClick={handleDelete} className="text-xs text-red-400/60 hover:text-red-300 transition-colors">Delete</button>
      </div>
    </div>
  );
}

export default HomebrewContentCard;
